import axios from 'axios';
import {
  API_BASE_PRESCRIPTION_URL,
  API_PRESCRIPTION_LIST,
  API_PRESCRIPTION_DETAILS,
  API_PRESCRIPTION_MOCK,
  API_PRESCRIPTION_CREATE,
  API_PRESCRIPTION_UPDATE,
  API_PRESCRIPTION_DELETE,
  API_PRESCRIPTION_TOGGLE_STATUS,
  API_DOCTORS,
  API_PATIENTS,
  API_MEDICINES
} from '../../../helpers/urlHelper';

// Crear una instancia de axios con la configuración base
const prescriptionApi = axios.create({
  baseURL: API_BASE_PRESCRIPTION_URL,
});

// Agregar interceptor de solicitud para manejar tokens (opcional)
prescriptionApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Función para transformar la respuesta al formato esperado por el frontend
const transformResponse = (response) => {
  // Si la respuesta ya tiene la estructura correcta (del controlador específico de prescripciones)
  if (response.data && response.data.hasOwnProperty('status')) {
    return response.data;
  }
  
  // Si la respuesta viene del controlador genérico, transformarla al formato esperado
  return {
    status: true,
    data: response.data,
    message: 'Operación completada exitosamente'
  };
};

// Función para manejar errores de manera consistente
const handleError = (error, customMessage) => {
  console.error(customMessage, error);
  
  // Si el error ya viene con el formato esperado
  if (error.response?.data?.status === false) {
    throw error.response.data;
  }
  
  // CAMBIO: Mejor manejo de errores de validación
  if (error.response?.data?.error) {
    const errorObj = {
      status: false,
      message: error.response.data.error,
      details: error.response.data.details || null
    };
    throw errorObj;
  }
  
  // Transformar cualquier otro error al formato esperado
  const errorObj = {
    status: false,
    message: customMessage,
    originalError: error.message || 'Error desconocido'
  };
  throw errorObj;
};

// Servicio de prescripciones
const prescriptionService = {
  // Obtener todas las prescripciones
  getAllPrescriptions: async () => {
    try {
      const response = await prescriptionApi.get(API_PRESCRIPTION_LIST);
      return transformResponse(response);
    } catch (error) {
      handleError(error, 'Error al obtener prescripciones');
    }
  },

  // Obtener prescripciones de prueba (para testing)
  getMockPrescriptions: async () => {
    try {
      const response = await prescriptionApi.get(API_PRESCRIPTION_MOCK);
      return transformResponse(response);
    } catch (error) {
      handleError(error, 'Error al obtener prescripciones de prueba');
    }
  },

  // Obtener una prescripción por ID
  getPrescriptionById: async (id) => {
    try {
      const response = await prescriptionApi.get(`${API_PRESCRIPTION_DETAILS}/${id}`);
      return transformResponse(response);
    } catch (error) {
      handleError(error, `Error al obtener la prescripción con id ${id}`);
    }
  },

  
  // Crear una nueva prescripción
  createPrescription: async (prescriptionData) => {
    try {
      const response = await prescriptionApi.post(API_PRESCRIPTION_CREATE, prescriptionData);
      return transformResponse(response);
    } catch (error) {
      handleError(error, 'Error al crear la prescripción');
    }
  },

  // Actualizar una prescripción existente
  updatePrescription: async (id, prescriptionData) => {
    try {
      const response = await prescriptionApi.put(`${API_PRESCRIPTION_UPDATE}/${id}`, prescriptionData);
      return transformResponse(response);
    } catch (error) {
      handleError(error, `Error al actualizar la prescripción con id ${id}`);
    }
  },

  // Actualizar el estado activo de una prescripción
  updatePrescriptionActiveStatus: async (id, isActive) => {
    try {
      const data = { isActive };
      const response = await prescriptionApi.patch(`${API_PRESCRIPTION_TOGGLE_STATUS}/${id}/toggle-status`, data);
      return transformResponse(response);
    } catch (error) {
      handleError(error, `Error al actualizar el estado activo para la prescripción con id ${id}`);
    }
  },

  // Eliminar una prescripción
  deletePrescription: async (id) => {
    try {
      const response = await prescriptionApi.delete(`${API_PRESCRIPTION_DELETE}/${id}`);
      return transformResponse(response);
    } catch (error) {
      handleError(error, `Error al eliminar la prescripción con id ${id}`);
    }
  },

  // Obtener todos los médicos
  getAllDoctors: async () => {
    try {
      const response = await prescriptionApi.get(API_DOCTORS);
      return transformResponse(response);
    } catch (error) {
      handleError(error, 'Error al obtener médicos');
    }
  },

  // Obtener todos los pacientes
  getAllPatients: async () => {
    try {
      const response = await prescriptionApi.get(API_PATIENTS);
      return transformResponse(response);
    } catch (error) {
      handleError(error, 'Error al obtener pacientes');
    }
  },

  // Obtener todos los medicamentos
  getAllMedicines: async () => {
    try {
      const response = await prescriptionApi.get(API_MEDICINES);
      return transformResponse(response);
    } catch (error) {
      handleError(error, 'Error al obtener medicamentos');
    }
  }
};

export default prescriptionService;